import { useParams, Link } from 'react-router-dom';
import { SEOHead } from '@/components/SEOHead';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { idOrSlugColumn } from '@/lib/idOrSlug';
import { Layout } from '@/components/layout/Layout';
import { PropertyImageGallery } from '@/components/property-detail/PropertyImageGallery';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useEffect } from 'react';
import { trackView } from '@/lib/tracking';
import { useToast } from '@/hooks/use-toast';
import { leadApi } from '@/services/api';
import { useState, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { formatDistanceToNow } from 'date-fns';
import {
  LandPlot, MapPin, Phone, Mail, MessageCircle, User, Compass, Route,
  CornerDownRight, TreePine, ArrowLeft, Share2, Heart, Ruler, Grid3X3,
  Home, ChevronRight, Eye, Calendar, Copy, Printer, Shield,
  ExternalLink, CheckCircle2, ChevronDown, ChevronUp,
} from 'lucide-react';
import { ReportButton } from '@/components/ReportButton';
import { MobileContactActionBar } from '@/components/property-detail/mobile/MobileContactActionBar';
import { MobileSectionTabs } from '@/components/property-detail/mobile/MobileSectionTabs';
import { MobileLeadFormSection } from '@/components/property-detail/mobile/MobileLeadFormSection';
import { MobileSafetyTips, MobileUsefulLinks, MobileReportListing } from '@/components/property-detail/mobile/MobileBottomBlocks';
import { useCurrency } from '@/contexts/CurrencyContext';
import { formatPrice as fmtPrice, formatPriceCompact } from '@/lib/formatPrice';

const SQM_TO_SQFT = 10.764;

export default function PlotDetail() {
  const { id } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { toast } = useToast();
  const lang = i18n.language || 'en';
  const [saved, setSaved] = useState(false);

  const { data: plot, isLoading } = useQuery({
    queryKey: ['plot-detail', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('plots')
        .select('*, plot_images(*)')
        .eq(idOrSlugColumn(id), id!)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!id,
  });

  useEffect(() => {
    if (!plot?.id) return;
    trackView({ listingType: 'plot', listingId: plot.id, city: plot.city });
  }, [plot?.id]);

  const { data: similarPlots } = useQuery({
    queryKey: ['similar-plots', plot?.city, plot?.listing_type, id],
    queryFn: async () => {
      const { data } = await supabase
        .from('plots')
        .select('*, plot_images(url, is_primary)')
        .eq('status', 'active')
        .eq('approval_status', 'approved')
        .eq('city', plot!.city)
        .eq('listing_type', plot!.listing_type)
        .neq('id', id!)
        .limit(4);
      return data || [];
    },
    enabled: !!plot,
  });

  const { data: ownerProfile } = useQuery({
    queryKey: ['plot-owner', plot?.user_id],
    queryFn: async () => {
      const { data } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', plot!.user_id)
        .maybeSingle();
      return data;
    },
    enabled: !!plot?.user_id,
  });

  const getName = (val: any) => (typeof val === 'object' ? val?.[lang] || val?.en || '' : val || '');

  const handleSave = async () => {
    if (!user) { toast({ variant: 'destructive', title: 'Please login to save' }); return; }
    setSaved(!saved);
    toast({ title: saved ? 'Removed from saved' : 'Plot saved!' });
  };

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) { navigator.share({ title: 'Plot', url }); }
    else { await navigator.clipboard.writeText(url); toast({ title: 'Link copied!' }); }
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="container py-6 space-y-6">
          <Skeleton className="h-[460px] rounded-xl" />
          <div className="grid md:grid-cols-[1fr_360px] gap-6">
            <div className="space-y-4"><Skeleton className="h-8 w-2/3" /><Skeleton className="h-5 w-1/3" /><Skeleton className="h-40 rounded-lg" /></div>
            <Skeleton className="h-[500px] rounded-lg" />
          </div>
        </div>
      </Layout>
    );
  }

  if (!plot) {
    return (
      <Layout>
        <div className="container py-20 text-center">
          <LandPlot className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
          <h1 className="text-2xl font-bold mb-2">Plot Not Found</h1>
          <p className="text-muted-foreground mb-6">This plot listing may have been removed or is no longer available.</p>
          <Button asChild><Link to="/plots"><ArrowLeft className="h-4 w-4 mr-2" />Back to Plots</Link></Button>
        </div>
      </Layout>
    );
  }

  const title = getName(plot.title);
  const description = getName(plot.description);
  const images = (plot.plot_images || []).sort((a: any, b: any) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
  const areaSqft = plot.area_sqm ? Math.round(Number(plot.area_sqm) * SQM_TO_SQFT) : null;
  const pricePerSqm = plot.area_sqm && plot.price ? Math.round(Number(plot.price) / Number(plot.area_sqm)) : null;
  const { displayCurrency, rate } = useCurrency();
  const formatPrice = (p: number) => fmtPrice(p, plot.currency || 'ETB', displayCurrency, rate);
  const timeAgo = plot.created_at ? formatDistanceToNow(new Date(plot.created_at), { addSuffix: true }) : '';
  const contactName = plot.contact_person || ownerProfile?.full_name || 'Plot Agent';
  const contactPhone = plot.contact_phone || ownerProfile?.phone || '+251911000000';

  // Gallery images formatted for PropertyImageGallery
  const galleryImages = images.map((img: any) => ({ url: img.url, is_primary: img.is_primary, sort_order: img.sort_order }));

  // Details table
  const detailsTable = [
    { label: 'Type', value: 'Land / Plot', label2: 'Purpose', value2: plot.listing_type === 'sale' ? 'For Sale' : 'For Rent' },
    { label: 'Price', value: formatPrice(Number(plot.price)), label2: 'Price per m²', value2: pricePerSqm ? formatPrice(pricePerSqm) : '-' },
    { label: 'Area (m²)', value: plot.area_sqm ? `${plot.area_sqm} m²` : '-', label2: 'Area (sqft)', value2: areaSqft ? `${areaSqft.toLocaleString()} sqft` : '-' },
    { label: 'Road Width', value: plot.road_width_ft ? `${plot.road_width_ft} ft` : '-', label2: 'Facing', value2: plot.facing_direction ? plot.facing_direction.replace('_', ' ') : '-' },
    { label: 'Plot Purpose', value: plot.plot_purpose || '-', label2: 'City', value2: `${plot.city}${plot.sub_city ? ', ' + plot.sub_city : ''}` },
    { label: 'Added', value: plot.created_at ? new Date(plot.created_at).toLocaleDateString() : '-', label2: 'ID', value2: (plot.reference_code || plot.id?.slice(0, 8) || '').toUpperCase() },
  ];

  // Plot features
  const plotFeatures = [
    plot.is_corner && { icon: CornerDownRight, label: 'Corner Plot' },
    plot.is_park_face && { icon: TreePine, label: 'Park Facing' },
    plot.is_main_road && { icon: Route, label: 'Main Road Access' },
    plot.area_sqm && { icon: Ruler, label: `Plot Size: ${plot.area_sqm} m²` },
    areaSqft && { icon: Grid3X3, label: `${areaSqft.toLocaleString()} sqft` },
    plot.facing_direction && { icon: Compass, label: `Facing: ${plot.facing_direction.replace('_', ' ')}` },
    plot.road_width_ft && { icon: Route, label: `Road: ${plot.road_width_ft} ft wide` },
    plot.plot_purpose && { icon: LandPlot, label: `Purpose: ${plot.plot_purpose}` },
  ].filter(Boolean) as { icon: any; label: string }[];

  return (
    <Layout>
      <SEOHead
        title={`${title} — Plot for ${plot.listing_type === 'sale' ? 'Sale' : 'Rent'} in ${plot.city}`}
        description={`${plot.area_sqm ? plot.area_sqm + ' m² ' : ''}plot for ${plot.listing_type} in ${plot.city}. ${formatPrice(Number(plot.price))}`}
        canonical={`/plot/${id}`}
        ogType="article"
        ogImage={{ type: 'plot', id: id!, badge: plot.listing_type === 'sale' ? 'Plot for Sale' : 'Plot for Rent' }}
      />
      {/* Breadcrumb */}
      <div className="bg-muted/50 border-b">
        <div className="container py-2.5">
          <nav className="flex items-center gap-1.5 text-xs text-muted-foreground overflow-x-auto">
            <Link to="/" className="hover:text-primary transition-colors flex items-center gap-1 shrink-0"><Home className="h-3 w-3" />Home</Link>
            <ChevronRight className="h-3 w-3 shrink-0" />
            <Link to="/plots" className="hover:text-primary transition-colors shrink-0">Plots & Land</Link>
            <ChevronRight className="h-3 w-3 shrink-0" />
            <Link to={`/plots?city=${plot.city}`} className="hover:text-primary transition-colors shrink-0">{plot.city}</Link>
            <ChevronRight className="h-3 w-3 shrink-0" />
            <span className="text-foreground truncate font-medium">{title}</span>
          </nav>
        </div>
      </div>

      <div className="container py-6">
        {/* Image Gallery */}
        <PropertyImageGallery images={galleryImages} title={title} isFeatured={plot.is_featured || false} />

        {/* Header section below gallery */}
        <div className="mt-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <Badge className="text-xs capitalize">{plot.listing_type === 'sale' ? 'For Sale' : 'For Rent'}</Badge>
                <Badge variant="outline" className="text-xs">Land / Plot</Badge>
                {plot.is_featured && <Badge variant="secondary" className="text-xs">Featured</Badge>}
                {plot.plot_purpose && <Badge variant="outline" className="text-xs capitalize">{plot.plot_purpose}</Badge>}
              </div>

              <div className="flex items-baseline gap-2 mb-2 flex-wrap min-w-0">
                <span className="font-display text-2xl sm:text-3xl md:text-4xl font-bold text-foreground break-words">{formatPrice(Number(plot.price))}</span>
                {plot.listing_type === 'rent' && <span className="text-base sm:text-lg text-muted-foreground">/month</span>}
              </div>

              <div className="flex items-start gap-1.5 text-muted-foreground mb-3 min-w-0">
                <MapPin className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                <span className="text-sm break-words min-w-0">{plot.city}{plot.sub_city ? ` - ${plot.sub_city}` : ''}{plot.woreda ? `, Woreda ${plot.woreda}` : ''}{plot.address ? `, ${plot.address}` : ''}</span>
              </div>

              {/* Quick specs */}
              <div className="flex items-center gap-x-6 gap-y-2 text-sm text-muted-foreground flex-wrap min-w-0">
                {plot.area_sqm && (
                  <span className="flex items-center gap-1.5">
                    <Ruler className="h-4 w-4" />
                    <strong className="text-foreground">{plot.area_sqm} m²</strong>
                    <span className="text-xs">({areaSqft?.toLocaleString()} sqft)</span>
                  </span>
                )}
                {plot.road_width_ft && (
                  <span className="flex items-center gap-1.5">
                    <Route className="h-4 w-4" />
                    <strong className="text-foreground">{plot.road_width_ft} ft</strong> road
                  </span>
                )}
                {plot.facing_direction && (
                  <span className="flex items-center gap-1.5">
                    <Compass className="h-4 w-4" />
                    <strong className="text-foreground capitalize">{plot.facing_direction.replace('_', ' ')}</strong>
                  </span>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2 shrink-0 flex-wrap">
              <Button variant="outline" size="sm" onClick={handleSave} className={saved ? 'text-red-500 border-red-200' : ''}>
                <Heart className={`h-4 w-4 sm:mr-1.5 ${saved ? 'fill-red-500' : ''}`} /><span className="hidden sm:inline">{saved ? 'Saved' : 'Save'}</span>
              </Button>
              <Button variant="outline" size="sm" onClick={handleShare}><Share2 className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">Share</span></Button>
              <Button variant="outline" size="sm" onClick={() => window.print()}><Printer className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">Print</span></Button>
            </div>
          </div>

          <div className="flex items-center gap-x-4 gap-y-1 mt-3 text-[11px] text-muted-foreground flex-wrap">
            {timeAgo && <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />Added {timeAgo}</span>}
            {plot.view_count != null && <span className="flex items-center gap-1"><Eye className="h-3 w-3" />{plot.view_count} views</span>}
            <span className="flex items-center gap-1 font-mono"><Copy className="h-3 w-3" />ID: {(plot.reference_code || `ID${plot.id?.slice(0, 8)}`).toUpperCase()}</span>
          </div>
        </div>

        <Separator className="mb-6" />

        {/* Two-column layout */}
        <div className="grid lg:grid-cols-[1fr_360px] gap-6">
          {/* Left: Tabbed Content */}
          <PlotDetailsSection plot={plot} title={title} description={description} detailsTable={detailsTable} plotFeatures={plotFeatures} formatPrice={formatPrice} />

          {/* Right: Sticky sidebar */}
          <div className="order-first lg:order-last">
            <div className="lg:sticky lg:top-[100px]">
              <PlotContactSidebar plot={plot} plotId={id!} contactName={contactName} contactPhone={contactPhone} />
            </div>
          </div>
        </div>

        {/* Similar Plots */}
        {similarPlots && similarPlots.length > 0 && (
          <div className="mt-12">
            <h2 className="font-display text-xl font-bold mb-6">Similar Plots in {plot.city}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {similarPlots.map((sp: any) => {
                const spTitle = getName(sp.title);
                const spImg = sp.plot_images?.find((i: any) => i.is_primary)?.url || sp.plot_images?.[0]?.url;
                return (
                  <Link key={sp.id} to={`/plot/${sp.id}`}>
                    <Card className="overflow-hidden hover:shadow-lg transition-all group cursor-pointer h-full">
                      <div className="relative h-40 bg-muted">
                        {spImg ? <img src={spImg} alt={spTitle} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                          : <div className="flex items-center justify-center h-full"><LandPlot className="h-10 w-10 text-muted-foreground" /></div>}
                        <Badge className="absolute top-2 left-2 bg-primary text-primary-foreground text-[10px]">{sp.listing_type === 'sale' ? 'For Sale' : 'For Rent'}</Badge>
                      </div>
                      <CardContent className="p-3 space-y-1">
                        <p className="font-bold text-primary">{sp.currency === 'USD' ? `$${Number(sp.price).toLocaleString()}` : `ETB ${Number(sp.price).toLocaleString()}`}</p>
                        <h3 className="font-semibold text-xs line-clamp-1">{spTitle}</h3>
                        <div className="flex items-center gap-1 text-[11px] text-muted-foreground"><MapPin className="h-3 w-3" />{sp.city}{sp.sub_city ? `, ${sp.sub_city}` : ''}</div>
                        {sp.area_sqm && <span className="text-[11px] text-muted-foreground">{sp.area_sqm} m²</span>}
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

/* ===== Plot Details Section with Tabs ===== */
function PlotDetailsSection({ plot, title, description, detailsTable, plotFeatures, formatPrice, mobileLayout = false }: any) {
  const [showFullDesc, setShowFullDesc] = useState(false);
  const descTooLong = description && description.length > 400;
  const displayDesc = showFullDesc ? description : description?.slice(0, 400);

  const overviewContent = (
    <div className="space-y-6 pt-6">
      <h2 className="text-xl font-bold">Overview</h2>
      <div>
        <h3 className="font-semibold text-base mb-3">Details</h3>
        <div className="border rounded-lg overflow-hidden">
          {detailsTable.map((row: any, i: number) => (
            <div key={i} className={`grid grid-cols-1 sm:grid-cols-2 ${i < detailsTable.length - 1 ? 'border-b' : ''}`}>
              <div className="grid grid-cols-[100px_1fr] sm:border-r border-b sm:border-b-0">
                <span className="text-sm text-muted-foreground py-2.5 px-3 bg-muted/30 font-medium">{row.label}</span>
                <span className="text-sm font-semibold py-2.5 px-3 capitalize break-words min-w-0">{row.value || '-'}</span>
              </div>
              <div className="grid grid-cols-[100px_1fr]">
                <span className="text-sm text-muted-foreground py-2.5 px-3 bg-muted/30 font-medium">{row.label2}</span>
                <span className="text-sm font-semibold py-2.5 px-3 capitalize break-words min-w-0">{row.value2 || '-'}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
      <Separator />
      {description && (
        <div>
          <h3 className="font-semibold text-base mb-3">Description</h3>
          <p className="text-sm text-muted-foreground whitespace-pre-line leading-relaxed">
            {displayDesc}{descTooLong && !showFullDesc && '...'}
          </p>
          {descTooLong && (
            <button onClick={() => setShowFullDesc(!showFullDesc)} className="text-primary text-sm font-semibold mt-2 hover:underline flex items-center gap-1">
              {showFullDesc ? 'Show Less' : 'Read More'}
              {showFullDesc ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
          )}
        </div>
      )}
      <Separator />
      <div>
        <h3 className="font-semibold text-base mb-4">Plot Features</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          {plotFeatures.map((f: any, i: number) => (
            <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-muted/50 border">
              <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                <f.icon className="h-4 w-4 text-primary" />
              </div>
              <span className="text-sm font-medium">{f.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const locationContent = (
    <div className="space-y-6 pt-6">
      <h2 className="text-xl font-bold">Location & Nearby</h2>
      <div className="rounded-xl overflow-hidden border bg-muted relative">
        {plot.latitude && plot.longitude ? (
          <iframe title="Plot Location" width="100%" height="400" style={{ border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade"
            src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${plot.latitude},${plot.longitude}&zoom=15`} />
        ) : (
          <div className="h-[300px] sm:h-[400px] flex flex-col items-center justify-center text-muted-foreground p-4 text-center">
            <MapPin className="h-10 w-10 mb-3 text-primary" />
            <p className="font-semibold text-lg">{plot.city}{plot.sub_city ? ` - ${plot.sub_city}` : ''}</p>
            <p className="text-sm mt-1">{plot.address || 'Exact location not available on map'}</p>
          </div>
        )}
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <Card><CardContent className="p-4">
          <h4 className="font-semibold text-sm mb-2">Address Details</h4>
          <div className="space-y-2 text-sm text-muted-foreground">
            <div className="flex justify-between gap-2"><span>City</span><span className="font-medium text-foreground">{plot.city}</span></div>
            {plot.sub_city && <div className="flex justify-between gap-2"><span>Sub City</span><span className="font-medium text-foreground">{plot.sub_city}</span></div>}
            {plot.woreda && <div className="flex justify-between gap-2"><span>Woreda</span><span className="font-medium text-foreground">{plot.woreda}</span></div>}
            {plot.address && <div className="flex justify-between gap-2"><span>Address</span><span className="font-medium text-foreground text-right break-words">{plot.address}</span></div>}
          </div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <h4 className="font-semibold text-sm mb-2">Plot Specifications</h4>
          <div className="space-y-2 text-sm text-muted-foreground">
            {plot.area_sqm && <div className="flex justify-between gap-2"><span>Area</span><span className="font-medium text-foreground text-right">{plot.area_sqm} m² ({Math.round(Number(plot.area_sqm) * SQM_TO_SQFT).toLocaleString()} sqft)</span></div>}
            {plot.road_width_ft && <div className="flex justify-between gap-2"><span>Road Width</span><span className="font-medium text-foreground">{plot.road_width_ft} ft</span></div>}
            {plot.facing_direction && <div className="flex justify-between gap-2"><span>Facing</span><span className="font-medium text-foreground capitalize">{plot.facing_direction.replace('_', ' ')}</span></div>}
            {plot.plot_purpose && <div className="flex justify-between gap-2"><span>Purpose</span><span className="font-medium text-foreground capitalize">{plot.plot_purpose}</span></div>}
          </div>
        </CardContent></Card>
      </div>
    </div>
  );

  const homeFinanceContent = (
    <div className="space-y-6 pt-6">
      <h2 className="text-xl font-bold">Home Finance</h2>
      <p className="text-muted-foreground text-sm">Calculate and view the monthly mortgage on this plot</p>
      <PlotMortgageCalculator price={Number(plot.price)} currency={plot.currency || 'ETB'} />
    </div>
  );

  const priceIndexContent = (
    <div className="pt-6">
      <PlotPriceIndex price={Number(plot.price)} currency={plot.currency || 'ETB'} />
    </div>
  );

  if (mobileLayout) {
    return (
      <div className="space-y-8">
        <section id="overview" className="scroll-mt-[120px]">{overviewContent}</section>
        <Separator />
        <section id="nearby" className="scroll-mt-[120px]">{locationContent}</section>
        <Separator />
        <section id="home-finance" className="scroll-mt-[120px]">{homeFinanceContent}</section>
        <Separator />
        <section id="price-index" className="scroll-mt-[120px]">{priceIndexContent}</section>
      </div>
    );
  }

  return (
    <div className="space-y-0">
      <Tabs defaultValue="overview" className="w-full">
        <div className="sticky top-[64px] z-30 bg-background border-b">
          <div className="overflow-x-auto no-scrollbar relative after:pointer-events-none after:absolute after:inset-y-0 after:right-0 after:w-8 after:bg-gradient-to-l after:from-background after:to-transparent">
            <TabsList className="inline-flex w-max justify-start rounded-none bg-transparent h-auto p-0 gap-0">
              {['overview', 'location', 'home-finance'].map(tab => (
                <TabsTrigger key={tab} value={tab}
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-3 sm:px-5 py-3 text-sm font-medium capitalize data-[state=active]:text-primary whitespace-nowrap">
                  {tab === 'overview' ? 'Overview' : tab === 'location' ? 'Location & Nearby' : 'Home Finance'}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>
        </div>

        <TabsContent value="overview" className="mt-0">
          {overviewContent}
          <Separator className="my-6" />
          {priceIndexContent}
        </TabsContent>

        <TabsContent value="location" className="mt-0">{locationContent}</TabsContent>
        <TabsContent value="home-finance" className="mt-0">{homeFinanceContent}</TabsContent>
      </Tabs>
    </div>
  );
}

/* ===== Contact Sidebar ===== */
function PlotContactSidebar({ plot, plotId, contactName, contactPhone }: { plot: any; plotId: string; contactName: string; contactPhone: string }) {
  const { toast } = useToast();
  const refId = (plot.reference_code || `ID${plotId.slice(0, 8)}`).toUpperCase();
  const defaultMsg = `I would like to inquire about your plot EthioRealEstates - ${refId}. Please contact me at your earliest convenience.`;
  const [form, setForm] = useState({ name: '', phone: '', email: '', message: defaultMsg });
  const [submitting, setSubmitting] = useState(false);
  const [phoneRevealed, setPhoneRevealed] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await leadApi.create({ name: form.name, phone: form.phone, email: form.email || undefined, message: form.message || undefined, plot_id: plotId, source: 'website' });
      toast({ title: 'Inquiry sent!', description: 'The agent will contact you shortly.' });
      setForm({ name: '', phone: '', email: '', message: defaultMsg });
    } catch (err: any) {
      toast({ variant: 'destructive', title: 'Failed to send', description: err.message });
    }
    setSubmitting(false);
  };

  const handleAction = (action: string) => {
    if (action === 'call') window.open(`tel:${contactPhone}`, '_self');
    else if (action === 'whatsapp') window.open(`https://wa.me/${contactPhone.replace(/[^0-9]/g, '')}`, '_blank');
    else if (action === 'email' && plot.contact_email) window.open(`mailto:${plot.contact_email}`, '_self');
  };

  return (
    <div className="space-y-4">
      <Card className="border-2 border-primary/20">
        <CardContent className="p-5">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-lg">
              {contactName.charAt(0)}
            </div>
            <div>
              <div className="font-semibold text-sm">{contactName}</div>
              <div className="text-xs text-muted-foreground flex items-center gap-1"><Shield className="h-3 w-3 text-primary" />Verified Agent</div>
            </div>
          </div>

          <div className="mb-4">
            {phoneRevealed ? (
              <a href={`tel:${contactPhone}`} className="flex items-center gap-2 p-3 bg-primary/5 rounded-lg border border-primary/20 text-primary font-semibold text-lg">
                <Phone className="h-5 w-5" />{contactPhone}
              </a>
            ) : (
              <Button variant="outline" className="w-full border-primary text-primary hover:bg-primary hover:text-primary-foreground font-semibold"
                onClick={() => setPhoneRevealed(true)}>
                <Phone className="h-4 w-4 mr-2" />{contactPhone.slice(0, 7)}••••  — Show Number
              </Button>
            )}
          </div>

          <div className="grid grid-cols-3 gap-2 mb-5">
            <Button className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold text-xs" onClick={() => handleAction('call')}><Phone className="h-3.5 w-3.5 mr-1" />Call</Button>
            <Button className="bg-green-600 hover:bg-green-700 text-white font-semibold text-xs" onClick={() => handleAction('whatsapp')}><MessageCircle className="h-3.5 w-3.5 mr-1" />WhatsApp</Button>
            <Button variant="outline" className="font-semibold text-xs" onClick={() => handleAction('email')}><Mail className="h-3.5 w-3.5 mr-1" />Email</Button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="text-sm font-semibold mb-2">Send a Message</div>
            <div className="space-y-1.5">
              <Label htmlFor="p-name" className="text-xs">Name *</Label>
              <Input id="p-name" required placeholder="Your name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="h-9 text-sm" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="p-phone" className="text-xs">Phone *</Label>
              <Input id="p-phone" type="tel" required placeholder="Your phone number" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className="h-9 text-sm" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="p-email" className="text-xs">Email</Label>
              <Input id="p-email" type="email" placeholder="Your email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className="h-9 text-sm" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="p-msg" className="text-xs">Message</Label>
              <Textarea id="p-msg" rows={3} placeholder="I'm interested in this plot..." value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} className="text-sm" />
            </div>
            <Button type="submit" className="w-full font-semibold" disabled={submitting}>{submitting ? 'Sending...' : 'Send Inquiry'}</Button>
          </form>
        </CardContent>
      </Card>

      {/* Useful Links */}
      <Card>
        <CardContent className="p-5">
          <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3 text-center">Useful Links</div>
          <div className="space-y-2">
            <Link to={`/plots?city=${plot.city}`} className="block text-sm text-primary hover:underline">Plots for sale in {plot.city}</Link>
            {plot.sub_city && <Link to={`/plots?city=${plot.city}`} className="block text-sm text-primary hover:underline">Plots in {plot.sub_city}</Link>}
            <Link to="/buy" className="block text-sm text-primary hover:underline">Properties for sale</Link>
            <Link to="/projects" className="block text-sm text-primary hover:underline">New Projects</Link>
          </div>
        </CardContent>
      </Card>

      {/* Report */}
      <Card className="bg-muted/50">
        <CardContent className="p-4">
          <ReportButton
            targetType="plot"
            targetId={plotId}
            variant="outline"
            className="w-full border-destructive/30 text-destructive hover:bg-destructive hover:text-destructive-foreground font-semibold text-sm"
            label="Report this listing"
          />
        </CardContent>
      </Card>

      {/* Safety tip */}
      <Card className="bg-muted/50">
        <CardContent className="p-4">
          <div className="flex items-start gap-2">
            <Shield className="h-5 w-5 text-primary shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-xs mb-1">Safety Tips</div>
              <ul className="text-[11px] text-muted-foreground space-y-0.5">
                <li>• Don't pay any advance without verifying</li>
                <li>• Meet the seller at a safe public place</li>
                <li>• Verify the land documents before paying</li>
                <li>• Confirm the plot boundaries on-site</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/* ===== Price Index ===== */
function PlotPriceIndex({ price, currency }: { price: number; currency: string }) {
  const [timeRange, setTimeRange] = useState<'6m' | '1y' | 'max'>('max');
  const fmt = (n: number) => currency === 'USD' ? `$${n.toLocaleString()}` : `ETB ${n.toLocaleString()}`;

  const allData = useMemo(() => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const data = [];
    const now = new Date();
    for (let i = 24; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const drift = 1 + (24 - i) * 0.012 + Math.sin(i * 0.5) * 0.03;
      data.push({ month: `${months[d.getMonth()]} ${d.getFullYear().toString().slice(2)}`, price: Math.round(price * drift * (0.85 + Math.random() * 0.05)) });
    }
    return data;
  }, [price]);

  const chartData = timeRange === '6m' ? allData.slice(-7) : timeRange === '1y' ? allData.slice(-13) : allData;
  const currentP = chartData[chartData.length - 1]?.price || price;
  const oldestP = chartData[0]?.price || price;
  const change = currentP - oldestP;
  const changePct = oldestP > 0 ? ((change / oldestP) * 100).toFixed(1) : '0';

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-base">Price Trends</h3>
        <div className="flex gap-1">
          {(['6m', '1y', 'max'] as const).map(r => (
            <Button key={r} size="sm" variant={timeRange === r ? 'default' : 'outline'} className="text-xs h-7 px-3" onClick={() => setTimeRange(r)}>
              {r === '6m' ? '6 Months' : r === '1y' ? '1 Year' : 'All Time'}
            </Button>
          ))}
        </div>
      </div>
      <Card><CardContent className="p-4">
        <div className="flex items-center gap-4 mb-4">
          <div><div className="text-xs text-muted-foreground">Current Value</div><div className="font-bold text-lg">{fmt(currentP)}</div></div>
          <div><div className="text-xs text-muted-foreground">Change</div><div className={`font-bold ${change >= 0 ? 'text-green-600' : 'text-red-600'}`}>{change >= 0 ? '+' : ''}{changePct}%</div></div>
        </div>
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={chartData}><CartesianGrid strokeDasharray="3 3" className="stroke-muted" /><XAxis dataKey="month" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} tickFormatter={(v: number) => formatPriceCompact(v, 'ETB', 'ETB', 0.0078)} /><Tooltip formatter={(v: number) => fmt(v)} /><Line type="monotone" dataKey="price" className="stroke-primary" strokeWidth={2} dot={false} /></LineChart>
        </ResponsiveContainer>
      </CardContent></Card>
    </div>
  );
}

/* ===== Mortgage Calculator ===== */
function PlotMortgageCalculator({ price, currency }: { price: number; currency: string }) {
  const [downPayment, setDownPayment] = useState(20);
  const [loanTerm, setLoanTerm] = useState(20);
  const [interestRate, setInterestRate] = useState(14.5);
  const fmt = (n: number) => currency === 'USD' ? `$${Math.round(n).toLocaleString()}` : `ETB ${Math.round(n).toLocaleString()}`;

  const loanAmount = price * (1 - downPayment / 100);
  const monthlyRate = interestRate / 100 / 12;
  const numPayments = loanTerm * 12;
  const monthlyPayment = monthlyRate > 0 ? (loanAmount * monthlyRate * Math.pow(1 + monthlyRate, numPayments)) / (Math.pow(1 + monthlyRate, numPayments) - 1) : loanAmount / numPayments;
  const totalPayment = monthlyPayment * numPayments;
  const totalInterest = totalPayment - loanAmount;

  return (
    <div className="grid md:grid-cols-2 gap-6">
      <Card><CardContent className="p-5 space-y-5">
        <div><Label className="text-sm font-medium">Down Payment: {downPayment}%</Label><Slider value={[downPayment]} onValueChange={v => setDownPayment(v[0])} min={5} max={80} step={5} className="mt-2" /></div>
        <div><Label className="text-sm font-medium">Loan Term: {loanTerm} years</Label><Slider value={[loanTerm]} onValueChange={v => setLoanTerm(v[0])} min={5} max={30} step={5} className="mt-2" /></div>
        <div><Label className="text-sm font-medium">Interest Rate: {interestRate}%</Label><Slider value={[interestRate]} onValueChange={v => setInterestRate(v[0])} min={5} max={25} step={0.5} className="mt-2" /></div>
      </CardContent></Card>

      <Card className="border-primary/20"><CardContent className="p-5 space-y-4">
        <div className="text-center"><div className="text-xs text-muted-foreground mb-1">Monthly Payment</div><div className="text-3xl font-bold text-primary">{fmt(monthlyPayment)}</div></div>
        <Separator />
        <div className="space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-muted-foreground">Plot Price</span><span className="font-medium">{fmt(price)}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Down Payment</span><span className="font-medium">{fmt(price * downPayment / 100)}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Loan Amount</span><span className="font-medium">{fmt(loanAmount)}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Total Interest</span><span className="font-medium">{fmt(totalInterest)}</span></div>
          <div className="flex justify-between font-semibold border-t pt-2"><span>Total Payment</span><span>{fmt(totalPayment)}</span></div>
        </div>
      </CardContent></Card>
    </div>
  );
}
